import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { User, Star, Users, Crown, Check } from "lucide-react";
import { TICKETS } from "../config";
import { trackEvent, trackViewContent } from "../tracking";
import { useCountdown } from "../hooks/useCountdown";
import { useReveal } from "../hooks/useReveal";

const TIER_META = [
  { label: "Level 01", Icon: User },
  { label: "Popular", Icon: Star },
  { label: "Exclusive", Icon: Users },
  { label: "Premium", Icon: Crown },
];


export default function Tickets() {
  const navigate = useNavigate();
  const { isEarlyBird } = useCountdown();
  const headRef = useReveal();
  const gridRef = useReveal();
  const tracked = useRef(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked.current) {
          tracked.current = true;
          trackViewContent("Tickets");
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  function handleClick(ticket) {
    trackEvent("InitiateCheckout", {
      content_name: ticket.name,
      value: isEarlyBird ? ticket.earlyPrice : ticket.regularPrice,
      currency: "USD",
    });
    navigate("/ram-raleigh-deal");
  }

  function formatPrice(n) {
    return n >= 1000 ? `$${n.toLocaleString()}` : `$${n}`;
  }

  return (
    <section ref={sectionRef} id="tickets" className="py-32 bg-[#080808]">
      <div className="container mx-auto px-6">
        <div ref={headRef} className="reveal mb-20 text-center">
          <h2 className="text-5xl md:text-7xl font-serif text-white">
            Pick your <span className="text-[#C9ED00] italic">spot</span>
          </h2>
          <p className="text-gray-500 text-lg mt-6 max-w-lg mx-auto">
            {isEarlyBird
              ? "Early bird pricing until April 18. Secure your spot at the most anticipated Telugu concert of the year."
              : "Tickets available — grab yours before they sell out."}
          </p>
        </div>

        <div ref={gridRef} className="reveal grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {TICKETS.map((ticket, i) => {
            const meta = TIER_META[i];
            const isFeatured = ticket.featured;

            if (isFeatured) {
              return (
                <div
                  key={ticket.id}
                  onClick={() => handleClick(ticket)}
                  onKeyDown={(e) => e.key === "Enter" && handleClick(ticket)}
                  role="link"
                  tabIndex={0}
                  aria-label={`${ticket.name} - ${formatPrice(isEarlyBird ? ticket.earlyPrice : ticket.regularPrice)}`}
                  className="reveal bg-amber-500 p-1 scale-105 rounded-[2.5rem] shadow-[0_30px_60px_-15px_rgba(201,237,0,0.3)] cursor-pointer"
                >
                  <div className="bg-amber-500 p-10 rounded-[2.4rem] flex flex-col h-full">
                    <div className="flex justify-between items-start mb-10">
                      <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center">
                        <meta.Icon className="w-5 h-5 text-black" />
                      </div>
                      <span className="text-[10px] font-bold text-black/40 tracking-widest uppercase border border-black/10 px-3 py-1 rounded-full">
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-3xl font-serif text-black mb-2">{ticket.name}</h3>
                      <p className="text-black/60 text-sm mb-8">{ticket.desc}</p>
                      <div className="flex items-baseline gap-2 mb-10">
                        <span className="text-5xl font-serif text-black">
                          {formatPrice(isEarlyBird ? ticket.earlyPrice : ticket.regularPrice)}
                        </span>
                        <span className="text-[10px] text-black/40 uppercase tracking-widest">
                          {isEarlyBird ? "Early Bird" : "Per Ticket"}
                        </span>
                      </div>
                      <ul className="space-y-4 mb-12">
                        {ticket.features.map((f) => (
                          <li key={f} className="flex items-center gap-3 text-sm text-black/80 font-medium">
                            <Check className="w-4 h-4 text-black" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="w-full py-4 text-center rounded-full bg-black text-amber-400 font-bold hover:bg-black/80 transition-all duration-300 shadow-xl">
                      {ticket.cta}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={ticket.id}
                onClick={() => handleClick(ticket)}
                onKeyDown={(e) => e.key === "Enter" && handleClick(ticket)}
                role="link"
                tabIndex={0}
                aria-label={`${ticket.name} - ${formatPrice(isEarlyBird ? ticket.earlyPrice : ticket.regularPrice)}`}
                className="reveal group bg-[#111] border border-white/5 p-10 rounded-[2.5rem] flex flex-col hover:border-amber-500/30 transition-all duration-500 shadow-xl cursor-pointer"
              >
                <div className="flex justify-between items-start mb-10">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center">
                    <meta.Icon className="w-5 h-5 text-white/50" />
                  </div>
                  <span className="text-[10px] font-bold text-white/30 tracking-widest uppercase border border-white/5 px-3 py-1 rounded-full">
                    {meta.label}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-serif text-white mb-2">{ticket.name}</h3>
                  <p className="text-white/40 text-sm mb-8">{ticket.desc}</p>
                  <div className="flex items-baseline gap-2 mb-10">
                    <span className="text-5xl font-serif text-amber-400">
                      {formatPrice(isEarlyBird ? ticket.earlyPrice : ticket.regularPrice)}
                    </span>
                    <span className="text-[10px] text-white/30 uppercase tracking-widest">
                      {isEarlyBird ? "Early Bird" : i === 2 ? "Per Table" : "Per Ticket"}
                    </span>
                  </div>
                  <ul className="space-y-4 mb-12">
                    {ticket.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-white/60">
                        <Check className="w-4 h-4 text-[#C9ED00]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="w-full py-4 text-center rounded-full border border-white/10 text-white font-semibold transition-all duration-300 group-hover:bg-white group-hover:text-black">
                  {ticket.cta}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
